<script setup>

import { inject, ref, computed, watch } from 'vue';
import { getErreurValeurListeRequise } from '@/scripts/controlesSaisie.js';
import Dialog from 'primevue/dialog';
import InputSwitch from 'primevue/inputswitch';
import Formulaire from '@/components/generiques/Formulaire.vue';
import SaisieTypeObjet from '@/components/generiques/SaisieTypeObjet.vue';
import SaisieListe from '@/components/generiques/SaisieListe.vue';
import ModaleEditionTypeObjet from '@/components/modales/ModaleEditionTypeObjet.vue';
import ModaleEditionChamp from '@/components/modales/ModaleEditionChamp.vue';

const emit = defineEmits(['ajoutVariableObjet', 'fermeture']);

const { colonnesSelectionnees, variables } = defineProps({
    colonnesSelectionnees: Array,
    variables: Array
});

const typesObjetMultiples = inject('typesObjetMultiples');
const getChampsCles = inject('getChampsCles');
const getTypeObjet = inject('getTypeObjet');

const variableObjet = ref({
    typeObjet: null,
    colonnesCles: [],
    objetsLies: [],
    creationObjet: false,
    reinitPopulation: false
});

const objetsLiesProposes = ref(getObjetsLiesProposes());
const nouveauTypeObjetSuggere = ref(null);
const nouveauChampLienSuggere = ref(null);

const typesObjetProposes = computed(() => {
    const typesObjetProposes = typesObjetMultiples.value.slice();
    typesObjetProposes.push({
        nom: '@nouveauTypeObjet',
        libelle: 'Nouveau type d\'objet...'
    });
    return typesObjetProposes;
});

const champsCles = computed(() => {
    if (variableObjet.value.typeObjet.nom !== '@nouveauTypeObjet') {
        return getChampsCles(variableObjet.value.typeObjet);
    } else {
        return [];
    }
});

watch(() => variableObjet.value.typeObjet, (typeObjet) => {
    if (typeObjet?.nom === '@nouveauTypeObjet') {
        const champsClesSuggeres = colonnesSelectionnees.map(colonne => {
            return {
                id: -1,
                nom: colonne.nom, 
                typeDonnee: colonne.typeDonnee
            };
        });
        nouveauTypeObjetSuggere.value = {
            typeOccurrence: 'PLUSIEURS',
            aCleAuto: (champsClesSuggeres.length === 0),
            champsCles: champsClesSuggeres
        };
        variableObjet.value.typeObjet = null;
    } else if (typeObjet) {
        variableObjet.value.colonnesCles = colonnesSelectionnees.slice(0, getChampsCles(typeObjet).length);
    } else {
        variableObjet.value.colonnesCles = [];
    }
});

watch(() => objetsLiesProposes.value.map(objetLie => objetLie.creationLien), (creationsLienApres, creationsLienAvant) => {
    creationsLienApres.forEach((creationLienApres, indice) => {
        const creationLienAvant = creationsLienAvant[indice];
        if (creationLienApres !== creationLienAvant) {
            const objetLie = objetsLiesProposes.value[indice];
            calculerChampsLienProposes(objetLie);
        }
    });
});

watch(() => objetsLiesProposes.value.map(objetLie => objetLie.champLien?.type), typesChampsLienApres => {
    typesChampsLienApres.forEach((typeChampLienApres, indice) => {
        if (typeChampLienApres === 'nouveau' && variableObjet.value.typeObjet) {
            objetsLiesProposes.value[indice].champLien = null;
            const typeObjet = variableObjet.value.typeObjet;
            const nomVariable = objetsLiesProposes.value[indice].nom;
            const variableObjetLie = variables.find(variable => variable.nom === nomVariable);
            nouveauChampLienSuggere.value = {
                idTypeObjet: variableObjetLie.typeDonnee.id,
                nom: `${typeObjet.nom.slice(0, 1).toLowerCase()}${typeObjet.nom.slice(1)}`,
                typeDonnee: {classe: 'OBJET', id: typeObjet.id},
                typeLien: 'APPARTIENT_A',
                nomChampReciproque: `${variableObjetLie.nom}s`,
                modeEvaluation: 'SAISIE',
                indiceObjetLie: indice
            };

        }
    });
});

watch(() => objetsLiesProposes.value.filter(objetLie => objetLie.creationLien), objetsLies => {
    variableObjet.value.objetsLies = objetsLies;
});

watch(() => variableObjet.value.creationObjet, (creation) => {
    if (!creation) {
        variableObjet.value.reinitPopulation = false;
    }
});

function getObjetsLiesProposes() {
    return variables.filter(variable => variable.typeDonnee?.classe === 'OBJET').map(variable => {
        return {nom: variable.nom, idTypeObjet: variable.typeDonnee.id, creationLien: false, champLien: null, champsLienProposes: []};
    });
}

function calculerChampsLienProposes(objetLie) {
    if (objetLie.creationLien) {
        objetLie.champsLienProposes = [];
        for (const champLien of getTypeObjet(objetLie.idTypeObjet).champs.filter(chp => chp.typeDonnee.classe === 'OBJET' && chp.typeDonnee.id === variableObjet.value.typeObjet.id)) {
            objetLie.champsLienProposes.push(champLien);
        }
        objetLie.champsLienProposes.push({nom: 'Nouveau champ de lien...', type: 'nouveau'});
    } else {
        objetLie.champsLienProposes = [];
    }
}

function validerSaisie() {
    const erreurs = {};
    erreurs.typeObjet = getErreurValeurListeRequise(variableObjet.value.typeObjet);
    for (const [indiceChampCle] of champsCles.value.entries()) {
        erreurs[`colonne_${indiceChampCle}`] = getErreurValeurListeRequise(variableObjet.value.colonnesCles[indiceChampCle]);
    }
    for (const objetLie of objetsLiesProposes.value) {
        erreurs[`champLien_${objetLie.nom}`] = getErreurValeurListeRequise(objetLie.champLien, objetLie.creationLien);
    }
    return erreurs;
}

function validerFormulaire() {
    variableObjet.value.validationFormulaire = true;
}

function ajouterVariableObjet() {
    const infosAjoutVariableObjet = {
        idTypeObjet: variableObjet.value.typeObjet.id,
        creationObjet: variableObjet.value.creationObjet,
        reinitPopulation: variableObjet.value.reinitPopulation,
        nomsColonnesCles: variableObjet.value.colonnesCles.map(col => col.nom),
        variablesObjetLie: variableObjet.value.objetsLies.map(objetLie => {
            return {nomVariable: objetLie.nom, idChampLien: objetLie.champLien.id};
        })
    };

    emit('ajoutVariableObjet', infosAjoutVariableObjet);
}

function gererAjoutChampLien(champLien, indiceObjetLie) {
    const objetLie = objetsLiesProposes.value[indiceObjetLie];
    calculerChampsLienProposes(objetLie);
    objetLie.champLien = champLien;
}

function gererFermetureModaleAjoutTypeObjet(typeObjetCree) {
    nouveauTypeObjetSuggere.value = null;
    variableObjet.value.typeObjet = typeObjetCree;
    variableObjet.value.creationObjet = true;
    variableObjet.value.reinitPopulation = true;
}

function gererFermeture(visible) {
    if (!visible) {
        emit('fermeture');
    }
}

</script>

<template>
    <Dialog 
        :visible="true" 
        modal 
        :header="'Définition d\'un objet à partir de colonnes du fichier'"
        @update:visible="gererFermeture">
            <div>
                <Formulaire
                    :objet="variableObjet"
                    :fonction-validation-saisie="validerSaisie"
                    @validation="ajouterVariableObjet">
                    <label class="intitule titre">Type d'objet</label>
                    <div class="ligne-formulaire">
                        <label class="intitule">Type d'objet :</label>
                        <SaisieTypeObjet 
                            class="liste-types-objet"
                            v-model:valeur="variableObjet.typeObjet" 
                            :id-erreur="'typeObjet'"
                            :typesObjet="typesObjetProposes"
                            :valeurOblig="true" />
                    </div>
                    <template v-if="variableObjet.typeObjet?.typeOccurrence === 'PLUSIEURS' && champsCles.length">
                        <label class="intitule titre">Identification de l'objet</label>
                        <div v-if="variableObjet.typeObjet"
                            class="tableau-colonnes-cles">
                            <label class="intitule">Champ clé</label>
                            <label class="intitule">Colonne</label>
                            <template v-for="(champCle, indiceChampCle) in champsCles">
                                <label class="intitule">{{ champCle.nom }}</label>
                                <SaisieListe 
                                    v-model:valeur="variableObjet.colonnesCles[indiceChampCle]" 
                                    :id-erreur="`colonne_${indiceChampCle}`" 
                                    :options="colonnesSelectionnees"
                                    :option-label="'nom'" />
                            </template>
                        </div>
                    </template>
                    <template v-if="variableObjet.typeObjet?.typeOccurrence === 'PLUSIEURS'">
                        <label class="intitule titre">Création d'objet</label>
                        <div class="zone-curseurs">
                            <InputSwitch 
                                class="curseur"
                                v-model="variableObjet.creationObjet" />
                            <label>Instancier l'objet s'il n'existe pas</label>
                        </div>
                        <div class="zone-curseurs">
                            <InputSwitch 
                                class="curseur"
                                :disabled="!variableObjet.creationObjet"
                                v-model="variableObjet.reinitPopulation" />
                            <label>Réinitialiser la population en début d'import</label>
                        </div>
                    </template>
                    <template v-if="variableObjet.typeObjet && objetsLiesProposes.length">
                        <label class="intitule titre titre-liens">Liens avec d'autres objets</label>
                        <template v-for="objetLie in objetsLiesProposes">
                            <div class="zone-curseurs">
                                <InputSwitch 
                                    class="curseur"
                                    v-model="objetLie.creationLien" />
                                <label>Lier à l'objet {{ objetLie.nom }}</label>
                                <SaisieListe 
                                    class="liste-champs-lien"
                                    :style="{visibility: (objetLie.creationLien ? 'visible' : 'hidden')}"
                                    v-model:valeur="objetLie.champLien" 
                                    :id-erreur="`champLien_${objetLie.nom}`" 
                                    :options="objetLie.champsLienProposes"
                                    :option-label="'nom'"
                                    placeholder="Champ de lien" />
                            </div>
                        </template>
                    </template>
                </Formulaire>
            </div>
            <template #footer>
                <button 
                    class="bouton fas fa-check" 
                    :disabled="false"
                    @click="validerFormulaire()"/>
            </template>
    </Dialog>
    <ModaleEditionTypeObjet 
        v-if="nouveauTypeObjetSuggere"
        :type-objet-edite="nouveauTypeObjetSuggere"
        @fermeture="(typeObjetCree) => gererFermetureModaleAjoutTypeObjet(typeObjetCree)" />
    <ModaleEditionChamp 
        v-if="nouveauChampLienSuggere"
        :champ-edite="nouveauChampLienSuggere"
        @fermeture="() => nouveauChampLienSuggere = null"
        @ajout-champ="(champLien) => gererAjoutChampLien(champLien, nouveauChampLienSuggere.indiceObjetLie)" />
</template>

<style scoped>

.formulaire {
    display: flex;
    flex-direction: column;
    align-items: stretch;
}

.ligne-formulaire {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
}

.liste-types-objet {
    flex-grow: 1;
}

.tableau-colonnes-cles {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 2px 5px;
    margin-bottom: 20px;
}

.zone-curseurs {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-grow: 1;
}

.liste-champs-lien {
    flex-grow: 1;
}

.titre-liens {
    margin-top: 20px;
}

</style>